import {ABILITY_CAST_COLORS as COLORS, ABILITY_CAST_TEXTURE_KEYS} from '../../configs/abilityCastAnimation';
import {MELEE_TEXTURE_KEYS} from '../../configs/meleeAnimation';
import {HEX_WAVE_TEXTURE_KEYS} from '../../configs/unitRecruitmentAnimation';
import {UNIT_DEATH_CAST} from '../../configs/unitDeathAnimation';
import {
  clamp01, createElement, easeOutBack, easeOutCubic, easeOutQuad, fadeInOut, lerp, mixColor, pushRisingSpiral,
} from './buildAbilityCast';
import {pushFlash, pushHexWave, pushPuff, pushStreak} from './buildCombatCast';
import {LIGHT_BEAM_ASPECT} from './hexWaveTextures';

const TWO_PI = Math.PI * 2;

/**
 * План гибели отряда (см. configs/unitDeathAnimation). Настоящего отряда на поле уже нет:
 * редьюсер убрал его вместе с итогами боя. Поэтому двойник стоит на месте с самого начала,
 * даже если очередь гибели в пачке еще не дошла (delayMs), а момент попадания — удар.
 *
 * @param {Object} params
 * @param {{center: {x, y}, body: {x, y}, ground: {x, y}}} params.unit — опорные точки ячейки
 * @param {number} params.hexSize
 * @param {number} params.color — цвет погибшего игрока, 0xRRGGBB
 * @param {{image: {textureKey, sizePx}, outline: ?{textureKey, sizePx}, tint: number}} [params.art]
 *   двойник отряда; нет — показывается только свет и осколки
 * @param {number} [params.tier] — старшинство типа отряда: чем выше, тем больше всего
 * @param {number} [params.delayMs] — очередь в пачке
 * @param {boolean} [params.isReduced] — без искр и дыма: гибнет сразу много отрядов
 * @param {number} [params.hexRotation]
 * @param {function(): number} [params.rng]
 */
export const buildUnitDeathCastPlan = ({
  unit, hexSize, color, art, tier = 1, delayMs = 0, isReduced = false, hexRotation = 0, rng = Math.random,
}) => {
  const {
    hitMs, collapseMs, tailMs, unit: unitPose, flash, stain, rays, wave, secondWave, shards, smoke, soul, stream, skull, shake,
  } = UNIT_DEATH_CAST;
  const size = hexSize;
  const {center, body, ground} = unit;
  const impactMs = delayMs + hitMs;
  const light = mixColor(color, COLORS.white, 0.6);
  const elements = [];

  // Пятно на земле: здесь только что стоял отряд
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.GLOW, tint: COLORS.black, sizePx: size * stain.sizeFactor,
    startMs: impactMs, durationMs: collapseMs + tailMs, layer: 'ground', order: -1,
    pose: (local) => ({...ground, scaleX: lerp(0.5, 1, easeOutCubic(clamp01(local * 3))), scaleY: lerp(0.3, 0.6, easeOutCubic(clamp01(local * 3))), alpha: stain.alpha * (1 - local)}),
  }));
  elements.push(createElement({
    textureKey: MELEE_TEXTURE_KEYS.BURST, tint: light, sizePx: size * rays.sizeFactor,
    startMs: impactMs, durationMs: rays.durationMs, layer: 'ground', order: 2,
    pose: (local) => ({
      ...center,
      scale: 0.3 + 0.7 * easeOutCubic(local),
      alpha: rays.alpha * fadeInOut(local, 0.1, 0.6),
      rotation: rays.turn * local,
    }),
  }));
  pushHexWave(elements, {at: center, hexSize: size, color, endFactor: wave.endFactor, durationMs: wave.durationMs, alpha: wave.alpha, startMs: impactMs, hexRotation, order: 3});
  if (tier >= secondWave.minTier) {
    pushHexWave(elements, {
      at: center, hexSize: size, color: COLORS.fireRed, endFactor: secondWave.endFactor, durationMs: secondWave.durationMs,
      alpha: secondWave.alpha, startMs: impactMs + secondWave.delayMs, hexRotation, order: 3.5,
    });
  }

  // ДВОЙНИК: вздрагивает от удара, белеет, затем темнеет и оседает
  if (art?.image) {
    const side = rng() < 0.5 ? -1 : 1;
    const doublePose = (elapsedMs) => {
      const sinceImpact = elapsedMs - impactMs;
      if (sinceImpact < 0) {
        const hit = clamp01((elapsedMs - delayMs) / hitMs);
        return {
          x: center.x + Math.sin(hit * Math.PI * 3) * size * unitPose.tremorFactor * hit,
          y: center.y,
          scale: lerp(1, unitPose.hitScale, easeOutQuad(hit)),
          alpha: 1,
          whiteShare: hit,
          ashShare: 0,
        };
      }
      const share = clamp01(sinceImpact / collapseMs);
      return {
        x: center.x,
        y: center.y + size * unitPose.sinkFactor * share * share,
        scale: lerp(unitPose.hitScale, unitPose.endScale, easeOutQuad(share)),
        rotation: side * unitPose.tilt * share * share,
        alpha: 1 - share * share,
        whiteShare: 1 - clamp01(share * 3.5),
        ashShare: clamp01(share * 2.5),
      };
    };
    const doubleMs = impactMs + collapseMs;
    if (art.outline) {
      elements.push(createElement({
        textureKey: art.outline.textureKey, sizePx: art.outline.sizePx, startMs: 0, durationMs: doubleMs, order: 4,
        pose: (local, elapsedMs) => {
          const {whiteShare, ashShare, ...pose} = doublePose(elapsedMs);
          return {...pose, tint: mixColor(COLORS.white, unitPose.ash, ashShare)};
        },
      }));
    }
    elements.push(createElement({
      textureKey: art.image.textureKey, tint: art.tint, sizePx: art.image.sizePx, startMs: 0, durationMs: doubleMs, order: 5,
      pose: (local, elapsedMs) => {
        const {whiteShare, ashShare, ...pose} = doublePose(elapsedMs);
        return {...pose, tint: mixColor(mixColor(art.tint, COLORS.white, whiteShare), unitPose.ash, ashShare)};
      },
    }));
  }

  pushFlash(elements, {at: body, tint: COLORS.white, sizePx: size * flash.sizeFactor, startMs: impactMs, durationMs: flash.durationMs, order: 6, blend: 'add'});

  // СТОЛБ СВЕТА уходит вверх и сужается
  const soulHeight = size * soul.heightFactor;
  [
    {widthPx: size * soul.widthFactor, tint: color, order: 3},
    {widthPx: size * soul.widthFactor * soul.coreWidthShare, tint: light, order: 3.5},
  ].forEach(({widthPx, tint, order}) => {
    elements.push(createElement({
      textureKey: HEX_WAVE_TEXTURE_KEYS.BEAM, tint, sizePx: widthPx, startMs: impactMs + soul.startMs, durationMs: soul.durationMs, order,
      pose: (local) => {
        const height = soulHeight * lerp(0.4, 1, easeOutCubic(local));
        return {
          x: center.x,
          y: ground.y - height / 2 - size * 0.35 * local,
          scaleX: lerp(1, 0.12, local * local),
          scaleY: height / (widthPx * LIGHT_BEAM_ASPECT),
          alpha: fadeInOut(local, 0.15, 0.55),
        };
      },
    }));
  });

  // Осколки по кругу остаются и в большой пачке: без них гибель не читается
  const shardsCount = isReduced ? shards.countBase : shards.countBase + shards.countPerTier * tier;
  for (let index = 0; index < shardsCount; index += 1) {
    const angle = index / shardsCount * TWO_PI + (rng() - 0.5) * 0.5;
    pushStreak(elements, {
      at: body, angle, distance: size * lerp(shards.minReachFactor, shards.maxReachFactor, rng()),
      lengthPx: size * shards.lengthFactor, thicknessPx: size * shards.thicknessFactor,
      tint: [light, color, COLORS.white][index % 3], startMs: impactMs, lifeMs: shards.lifeMs, order: 7,
    });
  }

  if (!isReduced) {
    const smokeCount = smoke.countBase + smoke.countPerTier * tier;
    for (let index = 0; index < smokeCount; index += 1) {
      const angle = index / smokeCount * TWO_PI + (rng() - 0.5) * 0.7;
      pushPuff(elements, {
        at: {x: center.x, y: center.y + Math.sin(angle) * size * smoke.spreadFactor * 0.4}, tint: index % 2 ? COLORS.smoke : COLORS.darkSmoke,
        sizePx: size * smoke.sizeFactor * (0.8 + 0.4 * rng()), startMs: impactMs + 40, lifeMs: smoke.lifeMs, alpha: smoke.alpha,
        shiftX: Math.cos(angle) * size * smoke.spreadFactor, riseY: size * 0.4, spin: (rng() - 0.5) * 0.9, order: 2,
      });
    }

    const spiral = [];
    pushRisingSpiral(spiral, {
      centerX: center.x, groundY: ground.y, topY: center.y - size * stream.topLiftFactor, size, order: 8, rng,
      particles: {...stream, count: stream.countBase + stream.countPerTier * tier},
      tints: [color, light, COLORS.white, light],
    });
    spiral.forEach((element) => {
      elements.push({...element, startMs: element.startMs + impactMs, endMs: element.endMs + impactMs});
    });
  }

  // ЧЕРЕП над ячейкой: издалека понятно, что отряд погиб, а не просто вспыхнул
  const skullStartMs = impactMs + skull.startMs;
  const skullPose = (local) => {
    const elapsed = local * skull.durationMs;
    return {
      x: center.x,
      y: center.y - size * lerp(skull.startLiftFactor, skull.endLiftFactor, easeOutQuad(local)),
      scale: elapsed < skull.popMs ? Math.max(0, easeOutBack(elapsed / skull.popMs)) : 1,
      alpha: fadeInOut(local, 0.08, 0.4),
    };
  };
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SKULL, tint: skull.outline, sizePx: size * skull.sizeFactor, startMs: skullStartMs, durationMs: skull.durationMs, order: 9,
    pose: (local) => {
      const pose = skullPose(local);
      return {...pose, scale: pose.scale * skull.outlineScale};
    },
  }));
  elements.push(createElement({
    textureKey: ABILITY_CAST_TEXTURE_KEYS.SKULL, tint: skull.tint, sizePx: size * skull.sizeFactor, startMs: skullStartMs, durationMs: skull.durationMs, order: 9.5,
    pose: skullPose,
  }));

  return {
    totalMs: elements.reduce((latest, element) => Math.max(latest, element.endMs), impactMs + tailMs),
    impactMs,
    elements,
    outlines: [],
    shake: tier >= shake.minTier ? {atMs: impactMs, durationMs: shake.durationMs, intensity: shake.intensity} : null,
  };
};
